import type { ReactNode } from "react";

import type { CloudBoardState } from "@board-preserve/contracts";

import { ArchiveIcon } from "./power-up-icons";
import {
  planName,
  storageAllowanceLabel,
  storageUsageLabel,
} from "./plan-and-usage-panel";

interface WorkspaceContextHeaderProps {
  historyPending?: boolean;
  cloudState: CloudBoardState | null;
  onPlan: () => void;
}

export function ProductMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`task-hub-mark${compact ? " task-hub-mark-compact" : ""}`}>
      <ArchiveIcon />
    </span>
  );
}

function usageLabel(
  cloudState: CloudBoardState | null,
  historyPending: boolean,
): string {
  if (!cloudState) return "See plans";
  if (cloudState.entitlements.effectivePlan !== "free") {
    if (!cloudState.usage) {
      return historyPending ? "Checking usage…" : "Usage unavailable";
    }
    return `${storageUsageLabel(cloudState.usage.workspaceStorageBytes)} of ${storageAllowanceLabel(cloudState.entitlements.workspaceStorageLimitBytes)}`;
  }
  if (cloudState.trial.status === "available") return "1 free backup left";
  if (cloudState.trial.status === "reserved") return "Backup in progress";
  return "Trial used";
}

export function WorkspaceContextHeader({
  historyPending = false,
  cloudState,
  onPlan,
}: WorkspaceContextHeaderProps) {
  return (
    <header className="task-hub-context">
      <div className="task-hub-context-title">
        <ProductMark />
        <div>
          <strong>{cloudState?.board.name ?? "This Trello board"}</strong>
          <span>
            {cloudState?.workspace.name ?? "Board Preserve by Nodestone Labs"}
          </span>
        </div>
      </div>
      <button className="task-hub-plan-summary" onClick={onPlan} type="button">
        <span>
          {cloudState
            ? planName(cloudState.entitlements.effectivePlan)
            : "Plans"}
        </span>
        <small>{usageLabel(cloudState, historyPending)}</small>
      </button>
    </header>
  );
}

export function WorkspaceBootstrap({
  failed = false,
  onRetry,
}: {
  failed?: boolean;
  onRetry: () => void;
}) {
  return (
    <section className="workspace-bootstrap" aria-live="polite">
      <header className="task-hub-context task-hub-context-loading">
        <ProductMark />
        <div>
          <span className="bootstrap-line bootstrap-line-title" />
          <span className="bootstrap-line bootstrap-line-subtitle" />
        </div>
      </header>
      <div className="workspace-bootstrap-body" role="status">
        {failed ? (
          <>
            <strong>Board protection could not be checked</strong>
            <p>
              Board Preserve has not assumed that this board is unprotected.
              Check the connection and try again.
            </p>
            <button onClick={onRetry} type="button">
              Try again
            </button>
          </>
        ) : (
          <>
            <span className="bootstrap-spinner" aria-hidden="true" />
            <strong>Checking this board’s protection…</strong>
            <p>Confirming Workspace access, plan and backup history.</p>
          </>
        )}
      </div>
    </section>
  );
}

interface TaskScreenHeaderProps {
  backLabel?: string;
  children?: ReactNode;
  onBack: () => void;
  subtitle: string;
  title: string;
  titleId?: string;
}

export function TaskScreenHeader({
  backLabel = "Back to board overview",
  children,
  onBack,
  subtitle,
  title,
  titleId,
}: TaskScreenHeaderProps) {
  return (
    <header className="task-screen-header">
      <button className="task-back-link" onClick={onBack} type="button">
        <span aria-hidden="true">‹</span> {backLabel}
      </button>
      <div className="task-screen-title-row">
        <div>
          <h1 id={titleId}>{title}</h1>
          <p>{subtitle}</p>
        </div>
        {children ? (
          <div className="task-screen-actions">{children}</div>
        ) : null}
      </div>
    </header>
  );
}

interface TaskHubFooterProps {
  onPlan: () => void;
  onSettings: () => void;
}

export function TaskHubFooter({ onPlan, onSettings }: TaskHubFooterProps) {
  return (
    <footer className="task-hub-footer">
      <button onClick={onPlan} type="button">
        Plan and usage
      </button>
      <button onClick={onSettings} type="button">
        Settings
      </button>
    </footer>
  );
}
